import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Loader2, ImageUp } from "lucide-react";
import { toast } from "sonner";

export const SITO_DEFAULTS = {
  hero: {
    kicker: "Napoli · Vigilanza Zoofila e Ambientale · Protezione Civile",
    title: "Associazione Nazionale Guardie Ecozoofile ODV",
    subtitle: "Per la salvaguardia del territorio e la protezione degli animali. Unisciti a noi: entra a far parte delle Guardie Ecozoofile di Napoli.",
    join_url: "https://docs.google.com/forms/d/e/1FAIpQLSfqxEQwPbqKqLAUo4dWP9W__6rR3pbMzioOGLjpM0NcGKCIGQ/viewform",
  },
  chi: {
    kicker: "Chi siamo",
    title: "La nostra missione",
    items: [
      { t: "Tutela degli animali", d: "Vigilanza zoofila e contrasto al maltrattamento, nel rispetto della normativa vigente." },
      { t: "Ambiente e territorio", d: "Vigilanza ambientale e salvaguardia del territorio." },
      { t: "Parchi e aree protette", d: "Sorveglianza di parchi e aree naturali protette." },
      { t: "Educazione ambientale", d: "Sensibilizzazione di cittadini e scuole al rispetto di animali e natura." },
      { t: "Supporto alle Forze dell'Ordine", d: "Collaborazione con le istituzioni sul territorio." },
      { t: "Protezione Civile", d: "Volontari formati per l'assistenza alla popolazione." },
    ],
  },
  corsi: {
    kicker: "Formazione",
    title: "Corsi di formazione",
    text: "È aperta la campagna di iscrizione dei nuovi soci e la partecipazione ai corsi. I corsi si svolgono in gruppi di massimo 40 partecipanti presso la sede operativa di Viale della Liberazione 1, Napoli (Parco San Laise, ex Base NATO Bagnoli, Edificio P, stanza 100).",
    items: [
      "Operatore di Protezione Civile",
      "Operatore Antincendio — corso base (basso e medio rischio)",
      "Operatore Antincendio — corso avanzato (alto rischio e idoneità tecnica VV.F.)",
      "Guardia EcoZoofila",
    ],
  },
  moduli: { kicker: "Modulistica", title: "Documenti per l'iscrizione", intro: "Stampa, compila e invia i moduli all'indirizzo email dell'Associazione, oppure iscriviti online." },
  galleria: { kicker: "Galleria", title: "Sul campo" },
  contatti: {
    kicker: "Contatti",
    title: "Dove trovarci",
    email: "info@angenapoli.it",
    pec: "presidenzanapoli@pec.angenapoli.it",
    sede_legale: "Via Pia 50, 80126 Napoli",
    sede_operativa: "Viale della Liberazione 1, Napoli — Parco San Laise (ex Base NATO Bagnoli), Edificio P, stanza 100",
    cf: "95248830630",
  },
  footer: { text: "A.N.G.E. — Associazione Nazionale Guardie Ecozoofile ODV · C.F. 95248830630 · Via Pia 50, 80126 Napoli" },
};

export const mergeSito = (db) => Object.fromEntries(
  Object.entries(SITO_DEFAULTS).map(([k, v]) => [k, { ...v, ...((db && db[k]) || {}) }]),
);

const T = (k, l) => ({ k, l });
const TA = (k, l) => ({ k, l, type: "textarea" });
export const SITO_SCHEMAS = {
  hero: { title: "Intestazione", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo"), TA("subtitle", "Sottotitolo"), T("join_url", "Link iscrizione online")] },
  chi: { title: "Chi siamo", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo"), { k: "items", l: "Punti della missione", type: "pairs" }] },
  corsi: { title: "Corsi", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo"), TA("text", "Testo"), { k: "items", l: "Elenco corsi (uno per riga)", type: "list" }] },
  moduli: { title: "Modulistica", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo"), TA("intro", "Testo introduttivo")] },
  galleria: { title: "Galleria", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo")] },
  contatti: { title: "Contatti", fields: [T("kicker", "Sopratitolo"), T("title", "Titolo"), T("email", "Email"), T("pec", "PEC"), TA("sede_legale", "Sede Legale e Amministrativa"), TA("sede_operativa", "Sede Operativa"), T("cf", "Codice Fiscale")] },
  footer: { title: "Pi\u00e8 di pagina", fields: [TA("text", "Testo")] },
};

const PairsField = ({ value, onChange }) => (
  <div className="space-y-2">
    {value.map((it, i) => (
      <div key={i} className="flex gap-2 rounded-md border border-slate-200 p-2">
        <div className="flex-1 space-y-1">
          <Input value={it.t} placeholder="Titolo" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, t: e.target.value } : x)))} data-testid={`edit-pair-t-${i}`} />
          <Textarea rows={2} value={it.d} placeholder="Descrizione" onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, d: e.target.value } : x)))} data-testid={`edit-pair-d-${i}`} />
        </div>
        <Button type="button" size="icon" variant="ghost" className="text-red-700" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`edit-pair-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    ))}
    <Button type="button" size="sm" variant="outline" onClick={() => onChange([...value, { t: "", d: "" }])} data-testid="edit-pair-add"><Plus className="mr-1 h-4 w-4" /> Aggiungi punto</Button>
  </div>
);

const Field = ({ f, value, onChange }) => {
  if (f.type === "pairs") { return <PairsField value={value || []} onChange={onChange} />; }
  if (f.type === "list") {
    return <Textarea rows={5} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n"))} data-testid={`edit-field-${f.k}`} />;
  }
  if (f.type === "textarea") { return <Textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`edit-field-${f.k}`} />; }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`edit-field-${f.k}`} />;
};

const HeroImageControl = ({ hasCustom, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    setBusy(true);
    try { await api.post("/sito/hero-image", { image: await resizeImage(file, 2000, 1400, 0.82) }); toast.success("Foto aggiornata"); onChanged(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    finally { setBusy(false); }
  };
  const reset = async () => { await api.delete("/sito/hero-image"); toast.success("Foto predefinita ripristinata"); onChanged(); };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-slate-300 p-3">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-md bg-slate-100 px-3 py-2 text-sm font-medium hover:bg-slate-200">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageUp className="h-4 w-4" />} Cambia foto di sfondo
        <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="edit-hero-image-input" />
      </label>
      {hasCustom && <Button type="button" size="sm" variant="ghost" onClick={reset} data-testid="edit-hero-image-reset">Ripristina predefinita</Button>}
    </div>
  );
};

export const SectionEditor = ({ section, data, open, onClose, onSaved, extra }) => {
  const schema = SITO_SCHEMAS[section];
  const [draft, setDraft] = useState(data);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...draft };
      if (Array.isArray(body.items)) {
        body.items = body.items.filter((x) => (typeof x === "string" ? x.trim() : (x.t || "").trim()));
      }
      delete body.updated_at; delete body.updated_by;
      await api.put(`/sito/${section}`, body);
      toast.success("Sezione aggiornata");
      onSaved();
      onClose();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Salvataggio non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid={`editor-${section}`}>
        <DialogHeader><DialogTitle>Modifica: {schema.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {extra}
          {schema.fields.map((f) => (
            <div key={f.k} className="space-y-1">
              <Label className="text-xs">{f.l}</Label>
              <Field f={f} value={draft[f.k]} onChange={(v) => setDraft({ ...draft, [f.k]: v })} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} className="bg-[#14382C] hover:bg-[#0E281F]" data-testid={`editor-${section}-save`}>
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const EditButton = ({ section, edit, onClick, dark }) => (edit ? (
  <button type="button" onClick={onClick} data-testid={`edit-section-${section}`}
    className={`absolute right-4 top-4 z-20 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-lg transition-transform hover:scale-105 ${dark ? "bg-white text-[#14382C]" : "bg-[#D97706] text-white"}`}>
    <Pencil className="h-3.5 w-3.5" /> Modifica
  </button>
) : null);

export { HeroImageControl };
